import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, RefreshCw, ChevronDown, ChevronUp, DollarSign, Wallet, User, Calendar } from 'lucide-react';
import { database, ref, get } from '../../firebase';

const FundTransfer = () => {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState([]);

  useEffect(() => {
    fetchAllTransfers();
  }, []);

  const fetchAllTransfers = async () => {
    setLoading(true);
    try {
      const transfersRef = ref(database, 'Transfers');
      const snapshot = await get(transfersRef);
      
      const allTransfers = [];
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        
        // Loop through date folders (2026-02-06, etc.)
        Object.entries(data).forEach(([date, users]) => {
          if (users && typeof users === 'object') {
            // Loop through users in each date folder
            Object.entries(users).forEach(([userId, userTransfers]) => {
              if (userTransfers && typeof userTransfers === 'object') {
                // Loop through individual transfers
                Object.entries(userTransfers).forEach(([transferId, transferData]) => {
                  if (transferData) {
                    allTransfers.push({
                      id: transferId,
                      transferId: transferData.id || transferId,
                      userId: transferData.userId || userId,
                      userName: transferData.userName || 'Unknown',
                      amount: parseFloat(transferData.amount) || 0,
                      fromWallet: transferData.fromWallet || '',
                      toWallet: transferData.toWallet || '',
                      date: transferData.date || new Date().toISOString(),
                      timestamp: transferData.timestamp || Date.now(),
                      status: transferData.status || 'completed',
                      userEmail: transferData.userEmail || '',
                      transferDate: transferData.transferDate || date,
                      dailyTransferCount: transferData.dailyTransferCount || 0,
                      dailyTransferAmount: transferData.dailyTransferAmount || 0,
                      // Balance info
                      previousFromBalance: parseFloat(transferData.previousFromBalance) || 0,
                      newFromBalance: parseFloat(transferData.newFromBalance) || 0,
                      previousToBalance: parseFloat(transferData.previousToBalance) || 0,
                      newToBalance: parseFloat(transferData.newToBalance) || 0
                    });
                  }
                });
              }
            });
          }
        });
      }
      
      // Sort by timestamp (newest first)
      allTransfers.sort((a, b) => b.timestamp - a.timestamp);
      setTransfers(allTransfers);
      
    } catch (error) {
      console.error('Error fetching transfers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter transfers based on search
  const filteredTransfers = transfers.filter(transfer => {
    const searchLower = searchTerm.toLowerCase();
    return !searchTerm || 
      (transfer.userId || '').toLowerCase().includes(searchLower) ||
      (transfer.userName || '').toLowerCase().includes(searchLower) ||
      (transfer.userEmail || '').toLowerCase().includes(searchLower);
  });

  // Calculate stats
  const totalAmount = filteredTransfers.reduce((sum, t) => sum + t.amount, 0);
  const completedCount = filteredTransfers.filter(t => t.status === 'completed').length;

  // Format date
  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'User', 'Email', 'Amount', 'From Wallet', 'To Wallet', 'Status', 'Transfer Date'];
    const csvData = filteredTransfers.map(t => [
      formatDate(t.timestamp),
      t.userName,
      t.userEmail,
      t.amount.toFixed(2),
      t.fromWallet,
      t.toWallet,
      t.status,
      t.transferDate
    ]);
    
    const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transfers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Toggle row expansion
  const toggleRow = (id) => {
    setExpandedRows(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const colors = {
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      failed: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs border ${colors[status] || 'bg-gray-500/20 text-gray-400'}`}>
        {status?.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
        <p className="text-muted-foreground">Loading transfers...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2">Internal Transfer Dashboard</h1>
        <p className="text-gray-400">All wallet transfers from users</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-400 text-sm">Total Transfers</div>
              <div className="text-xl font-bold text-white">{filteredTransfers.length}</div>
            </div>
            <Wallet className="h-5 w-5 text-blue-400" />
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-400 text-sm">Completed</div>
              <div className="text-xl font-bold text-green-400">{completedCount}</div>
            </div>
            <DollarSign className="h-5 w-5 text-green-400" />
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-400 text-sm">Total Amount</div>
              <div className="text-xl font-bold text-yellow-400">
                ${totalAmount.toFixed(2)}
              </div>
            </div>
            <DollarSign className="h-5 w-5 text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-gray-800 rounded-xl p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by user ID, name, or email..."
                className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={fetchAllTransfers}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Transfers List */}
      <div className="space-y-4">
        {filteredTransfers.map((transfer) => (
          <div key={transfer.id} className="bg-gray-800 rounded-xl overflow-hidden">
            <button
              onClick={() => toggleRow(transfer.id)}
              className="w-full p-4 flex justify-between items-center hover:bg-gray-700/50"
            >
              <div className="text-left flex-1">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-medium text-white">{transfer.userName}</div>
                    <div className="text-xs text-gray-400">{transfer.userId}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-white">${transfer.amount.toFixed(2)}</div>
                    <StatusBadge status={transfer.status} />
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm text-gray-400">
                  <div className="flex items-center">
                    <span className="text-red-400 mr-1">From:</span>
                    <span className="mr-4">{transfer.fromWallet}</span>
                    <span className="text-green-400 mr-1">To:</span>
                    <span>{transfer.toWallet}</span>
                  </div>
                  <div>{formatDate(transfer.timestamp)}</div>
                </div>
              </div>
              
              <div className="ml-2">
                {expandedRows.includes(transfer.id) ? (
                  <ChevronUp className="h-4 w-4 text-blue-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-blue-400" />
                )}
              </div>
            </button>
           
            {/* Expanded Details */}
            {expandedRows.includes(transfer.id) && (
              <div className="p-4 border-t border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <div className="text-gray-400 text-xs mb-1">Transfer ID</div>
                      <div className="text-white text-sm font-mono">{transfer.transferId}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-xs mb-1">User Email</div>
                      <div className="text-white text-sm">{transfer.userEmail}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-xs mb-1">Transfer Date</div>
                      <div className="text-white text-sm">{transfer.transferDate}</div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-gray-400 text-xs mb-1">From Balance</div>
                        <div className="text-white text-sm">
                          ${transfer.previousFromBalance.toFixed(2)} → ${transfer.newFromBalance.toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs mb-1">To Balance</div>
                        <div className="text-white text-sm">
                          ${transfer.previousToBalance.toFixed(2)} → ${transfer.newToBalance.toFixed(2)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-gray-400 text-xs mb-1">Daily Count</div>
                        <div className="text-white text-sm">{transfer.dailyTransferCount}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs mb-1">Daily Amount</div>
                        <div className="text-white text-sm">${transfer.dailyTransferAmount.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredTransfers.length === 0 && (
        <div className="text-center py-12 bg-gray-800 rounded-xl">
          <div className="text-4xl mb-4">💸</div>
          <h3 className="text-lg font-semibold text-white mb-2">No Transfers Found</h3>
          <p className="text-gray-400">No transfer records in the database</p>
          <button 
            onClick={fetchAllTransfers}
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
          >
            Refresh Data
          </button>
        </div>
      )}
    </div>
  );
};

export default FundTransfer;